import { Line } from "@ant-design/charts";
import {
  ArrowUpOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Empty,
  Result,
  Row,
  Skeleton,
  Space,
  Statistic,
  Typography,
  theme,
} from "antd";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { adminApi } from "@/api/client";
import { usePermissionsQuery } from "@/hooks/useAdminQueries";

function buildPlaceholderTrend() {
  return Array.from({ length: 7 }).map((_, i) => ({
    day: `D${i + 1}`,
    value: Math.round(20 + Math.sin(i) * 8),
  }));
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const permQ = usePermissionsQuery(true);
  const rbacReady = permQ.isSuccess;
  const canUsers =
    permQ.data?.includes("*") || permQ.data?.includes("users:read");

  const kpis = useQueries({
    queries: [
      {
        queryKey: ["admin", "kpi", "usersTotal"],
        enabled: rbacReady && canUsers,
        queryFn: async () => {
          const { data } = await adminApi.GET("/admin/v1/users", {
            params: { query: { page: 1, per_page: 1 } },
          });
          if (!data?.total && data?.total !== 0) throw new Error("no_users");
          return data.total;
        },
      },
      {
        queryKey: ["admin", "kpi", "nodesTotal"],
        enabled: rbacReady,
        queryFn: async () => {
          const { data } = await adminApi.GET("/admin/v1/nodes", {
            params: { query: { page: 1, per_page: 100 } },
          });
          if (!data?.items) throw new Error("no_nodes");
          const online = data.items.filter((n) => n.status === "online").length;
          return {
            total: data.total,
            onlineInPage: online,
            sampled: data.items.length,
          };
        },
      },
    ],
  });

  const overview = useQuery({
    queryKey: ["admin", "vpn", "nodesOverview"],
    queryFn: async () => {
      const { data, response } = await adminApi.GET(
        "/admin/v1/vpn/nodes/overview"
      );
      if (response.status >= 400) throw new Error("overview");
      return data as Record<string, unknown> | undefined;
    },
  });

  const usersTotal = kpis[0];
  const nodesAgg = kpis[1];

  const anyKpiError = kpis.some((q) => q.isError);
  const kpiLoading = kpis.some((q) => q.isPending);

  const chartData = buildPlaceholderTrend();
  const chartEmpty =
    !overview.data ||
    (typeof overview.data === "object" &&
      Object.keys(overview.data).length === 0);

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <div>
        <h2 style={{ marginTop: 0 }}>{t("dashboard.title")}</h2>
        <div style={{ color: token.colorTextSecondary }}>
          {t("dashboard.subtitle")}
        </div>
      </div>

      {anyKpiError ? (
        <Result
          status="warning"
          title={t("dashboard.loadErr")}
          extra={
            <Space>
              {kpis.map((q, i) => (
                <ReloadOutlined
                  key={i}
                  onClick={() => q.refetch()}
                  aria-label={t("common.retry")}
                />
              ))}
            </Space>
          }
        />
      ) : (
        <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            {kpiLoading && canUsers ? (
              <Skeleton active />
            ) : !canUsers ? (
              <Empty description={t("dashboard.noPermissionKpi")} />
            ) : (
              <Statistic
                title={t("dashboard.users")}
                value={usersTotal.data ?? 0}
                prefix={<ArrowUpOutlined />}
                suffix={
                  <span
                    style={{ fontSize: 12, color: token.colorTextSecondary }}
                  >
                    {t("dashboard.statTotalHint")}
                  </span>
                }
              />
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            {!rbacReady || kpiLoading ? (
              <Skeleton active />
            ) : nodesAgg.isError ? (
              <Empty description={t("dashboard.loadErr")} />
            ) : (
              <Statistic
                title={t("dashboard.nodesOnline")}
                value={nodesAgg.data?.onlineInPage ?? 0}
                suffix={
                  <span style={{ fontSize: 13 }}>
                    {t("dashboard.nodesOnlineDetail", {
                      total: nodesAgg.data?.total ?? "-",
                      sampled: nodesAgg.data?.sampled ?? 0,
                    })}
                  </span>
                }
              />
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t("dashboard.activeDevices")}
              value="—"
              valueStyle={{ fontSize: 18 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t("dashboard.conversion")}
              value="—"
              valueStyle={{ fontSize: 18 }}
            />
          </Card>
        </Col>
        </Row>
      )}

      <Card title={t("dashboard.chartTrend")}>
        {overview.isPending ? (
          <Skeleton.Input active style={{ height: 220 }} block />
        ) : overview.isError ? (
          <Result
            status="warning"
            title={t("dashboard.loadErr")}
            extra={
              <Button type="primary" onClick={() => overview.refetch()}>
                {t("common.retry")}
              </Button>
            }
          />
        ) : chartEmpty ? (
          <Empty description={t("dashboard.emptyChart")}>
            <TypographyHint />
            <div style={{ height: 200, marginTop: 24 }}>
              <Line
                data={chartData}
                xField="day"
                yField="value"
                height={200}
              />
            </div>
          </Empty>
        ) : (
          <Typography.Text type="secondary">
            {t("dashboard.chartBackendStub")}
          </Typography.Text>
        )}
      </Card>
    </Space>
  );
}

function TypographyHint() {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  return (
    <div style={{ color: token.colorTextSecondary, marginBottom: 8 }}>
      {t("dashboard.chartHint")}
    </div>
  );
}
